import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { JsonViewer } from 'components/Common/JsonViewer'
import { useLocalizedWorkingStepLabel } from 'components/Localization/useLocalizedWorkingStepLabel'
import { bomItemSelector } from 'features/BillOfMaterials/store/selectors/bomItemSelector'
import { useBomItemThumbnailImageSrc } from 'features/BillOfMaterials/useBomItemThumbnailImageSrc'
import { isEqual } from 'lodash'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { AssemblyHeaderRow, PartTypeRow } from 'model/Project/BoMItemRow'
import { useMemo } from 'react'
import {
  ProjectSupplierDto,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { useAppSelector } from 'store/configureStore'
import { makeStyles } from 'tss-react/mui'
import { RFQLine } from './ProjectRFQLines'

const useStyles = makeStyles({ name: 'ProjectRFQLine' })((theme) => ({
  lineItemName: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  activityList: {
    display: 'flex',
    gap: theme.spacing(0.5),
    flexWrap: 'wrap' as const,
    justifyContent: 'flex-end',
  },
}))

type ProjectRFQLineProps = {
  bomItemPointer: BomItemPointer
  line: RFQLine
  index: number
  onSelectionChange?: (bomItemId: string, checked: boolean) => void
  selected?: boolean
  supplier?: ProjectSupplierDto
  availableSuppliers?: ProjectSupplierDto[]
  onSupplierChange?: (
    bomItemId: string,
    supplier: ProjectSupplierDto | null,
  ) => void
  onActivitySelectionChange?: (
    bomItemId: string,
    activityIndex: number,
    selected: boolean,
  ) => void
}

export const ProjectRFQLine = ({
  bomItemPointer,
  line,
  index,
  onSelectionChange,
  selected,
  supplier,
  availableSuppliers,
  onSupplierChange,
  onActivitySelectionChange,
}: ProjectRFQLineProps) => {
  const { classes } = useStyles()
  const getLocalizedWorkingStepName = useLocalizedWorkingStepLabel()

  const bomItem = useAppSelector(
    (state) => bomItemSelector(state, bomItemPointer),
    isEqual,
  ) as AssemblyHeaderRow | PartTypeRow

  const thumbnailSrc = useBomItemThumbnailImageSrc({
    id: bomItem?.id,
    type: bomItem?.type,
  })

  const supplierActivitySet = useMemo(() => {
    if (!supplier?.activities) return new Set<WorkingStepType>()
    return new Set(
      supplier.activities
        .map((a) => a.workingStepType)
        .filter(Boolean) as WorkingStepType[],
    )
  }, [supplier])

  if (!bomItem) {
    return <JsonViewer data={{ bomItemPointer }} />
  }

  return (
    <TableRow
      key={bomItemPointer.id || `rfq-line-${index}`}
      hover
      selected={selected}
    >
      {onSelectionChange && (
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            checked={selected}
            onChange={(event) =>
              onSelectionChange?.(bomItemPointer.id || '', event.target.checked)
            }
          />
        </TableCell>
      )}
      <TableCell>
        <div className={classes.lineItemName}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ minWidth: 20, textAlign: 'right' }}
          >
            {bomItem.rowNumber}
          </Typography>
          <img
            src={thumbnailSrc.imageSrc}
            alt={bomItem.name}
            style={{ width: 28, height: 'auto', borderRadius: 2 }}
          />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {bomItem.name}
          </Typography>
        </div>
      </TableCell>
      {availableSuppliers && onSupplierChange && (
        <TableCell sx={{ minWidth: 180 }}>
          <Autocomplete
            size="small"
            value={supplier ?? null}
            options={availableSuppliers}
            getOptionLabel={(option) => option.name ?? ''}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            onChange={(_, value) => onSupplierChange(bomItemPointer.id, value)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Supplier..."
                variant="standard"
                size="small"
              />
            )}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start !important',
                  gap: 0.25,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {option.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.activities
                    ?.map((a) => getLocalizedWorkingStepName(a.workingStepType))
                    .join(' · ')}
                </Typography>
              </Box>
            )}
          />
        </TableCell>
      )}
      <TableCell align="right">
        {line.activities && line.activities.length > 0 ? (
          <Box className={classes.activityList}>
            {line.activities.map((activity, activityIndex) => {
              const isSupported =
                supplier && supplierActivitySet.has(activity.workingStepType)
              const isUnsupported = supplier && !isSupported
              const isSelected = activity.selected ?? true

              return (
                <Chip
                  key={`${bomItemPointer.id || index}-${activity.workingStepType || 'unknown'}-${activityIndex}`}
                  clickable={!!onActivitySelectionChange}
                  label={getLocalizedWorkingStepName(activity.workingStepType)}
                  onClick={() =>
                    onActivitySelectionChange?.(
                      bomItemPointer.id,
                      activityIndex,
                      !isSelected,
                    )
                  }
                  variant={isSelected ? 'filled' : 'outlined'}
                  color={isSelected ? 'primary' : 'default'}
                  sx={{
                    border: 1,
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    borderStyle: isUnsupported ? 'dashed' : 'solid',
                    fontWeight: isSelected ? 600 : 400,
                    '& .MuiChip-label': {
                      fontWeight: isSelected ? 600 : 400,
                    },
                  }}
                />
              )
            })}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No activities
          </Typography>
        )}
      </TableCell>
    </TableRow>
  )
}
